import React, { useContext, useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import searchIcon from "../../assets/search_icon.png";
import printIcon from "../../assets/printer_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import axios from "axios";
import OrderListWithInput from "./OrderListWithInput";
import FabricRulers from "./FabricRulers";
import SectionDivider from "../../components/SectionDivider";
import saveIcon from "../../assets/save_icon.png";
import useRequest from "../../utils/useRequest";
import { UserAuth } from "../../utils/AuthContext";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import PrintableOrderView from "./PrintableOrderView";
import AreYouSureModal from "../../components/AreYouSureModal";
import PageHeadline from "../../components/PageHeadline";
import useDebounce from "../../utils/useDebounce";

const AddFabricOrderPage = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const printRef = useRef(null);

  //  STATES
  const [selectedRulers, setSelectedRulers] = useState([]);
  const [selectedColors, setSelectedColors] = useState({});
  const [allSelectedRulers, setAllSelectedRulers] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    orderNumber: "",
    date: new Date().toLocaleDateString(),
    companyName: "",
    createdBy: "",
  });
  const [showAreYouSure, setAreYouSure] = useState(false);

  const [loadingRulers, setLoadingRuler] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [fabricData, setFabricData] = useState([]);
  const [orderData, setOrderData] = useState({ orderNumber: null, date: null });
  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "usd",
    value: "USD",
  });

  const [selectedMeasuringUnit, setSelectedMeasuringUnit] = useState({
    title: "meter",
    value: "METER",
  });

  /* ENDPOINTS */

  const {
    loading: confirmOrderLoading,
    success,
    error,
    setError,
    data,
    refetch: handleSaveOrder,
  } = useRequest(
    `/createFabricOrder`,
    "POST",
    {
      colors: allSelectedRulers
        ?.map((ruler) =>
          ruler?.colors.map((color) => ({
            ...color,
            measuringUnit: selectedMeasuringUnit.value,
            currency: selectedCurrency.value,
          }))
        )
        ?.flat(),
      organizationId: parseInt(userData?.organizationId),
      orderNumber: orderData?.orderNumber,
      createdAt: new Date(orderData?.date).toISOString(),
      measuringUnit: selectedMeasuringUnit.value,
      currency: selectedCurrency.value,
    },
    {},
    false
  );

  const getAllRulers = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingRuler(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllfabricRulers`,
        {
          params: {
            organizationId,
            code: searchQuery || undefined,
            page: pagination.currentPage,
            limit: 5,
          },
        }
      );

      if (response.status === 200 && response.data?.data) {
        const formatted = response.data.data.map((item) => ({
          id: item.id,
          code: item.code,
          colors: item.fabricColor.map((c) => ({
            id: c.id,
            colorCode: c.colorCode,
            colorName: c.colorName,
          })),
        }));
        setFabricData(formatted);
        setOrderData({
          orderNumber: response?.data?.orderNumber,
          date: response?.data?.date,
        });

        setOrderDetails((prev) => {
          return {
            orderNumber: response?.data?.orderNumber,
            companyName: userData.organizationName,
            createdBy: userData.username,
          };
        });

        setPagination({
          currentPage: response.data?.pagination?.page,
          totalPages: response.data?.pagination?.totalPages,
          totalRecords: response.data?.pagination?.total,
        });
      }
    } catch (err) {
      console.error("Failed to fetch rulers:", err);
    } finally {
      setLoadingRuler(false);
    }
  };

  /* HANDLER FUNCTIONS */
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { 
        size: A4; 
        margin: 5mm;
        width: 21cm;
        height: 29.7cm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact;
          padding: 10mm;
        }
        .printable-order-view { display: flex !important; }
      }
    `,
  });

  const handleConfirmOrder = () => {
    setAreYouSure(true);
  };

  const handlePrintAndSave = async () => {
    setAreYouSure(false);
    try {
      await handleSaveOrder();
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  // Updated color selection handler to maintain allSelectedRulers
  const handleColorSelection = (
    fabricCode,
    colorCode,
    isSelected,
    isFromSearch = false
  ) => {
    setSelectedColors((prev) => {
      if (colorCode === null) {
        const { [fabricCode]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [fabricCode]: {
          ...prev[fabricCode],
          [colorCode]: isSelected,
        },
      };
    });

    if (colorCode === null) {
      setAllSelectedRulers((prev) =>
        prev.filter((ruler) => ruler.code !== fabricCode)
      );
    } else {
      if (isSelected) {
        const fabric =
          fabricData.find((f) => f.code === fabricCode) ||
          allSelectedRulers.find((r) => r.code === fabricCode);

        if (fabric) {
          const color = fabric.colors.find((c) => c.colorCode === colorCode);

          if (color) {
            setAllSelectedRulers((prev) => {
              const rulerIndex = prev.findIndex((r) => r.code === fabricCode);

              if (rulerIndex >= 0) {
                const colorExists = prev[rulerIndex].colors.some(
                  (c) => c.colorCode === colorCode
                );

                if (!colorExists) {
                  const updatedRulers = [...prev];
                  updatedRulers[rulerIndex] = {
                    ...updatedRulers[rulerIndex],
                    colors: [
                      ...updatedRulers[rulerIndex].colors,
                      {
                        colorId: color.id,
                        colorCode: color.colorCode,
                        colorName: color.colorName,
                        fabricRulerId: fabric.id,
                        quantity: 0,
                        price: 0,
                      },
                    ],
                  };
                  return updatedRulers;
                }
                return prev;
              } else {
                return [
                  ...prev,
                  {
                    id: fabric.id,
                    code: fabricCode,
                    colors: [
                      {
                        colorId: color.id,
                        colorCode: color.colorCode,
                        colorName: color.colorName,
                        fabricRulerId: fabric.id,
                        quantity: 0,
                        price: 0,
                      },
                    ],
                  },
                ];
              }
            });
          }
        }
      } else {
        setAllSelectedRulers((prev) => {
          const updatedRulers = prev.map((ruler) => {
            if (ruler.code === fabricCode) {
              return {
                ...ruler,
                colors: ruler.colors.filter(
                  (color) => color.colorCode !== colorCode
                ),
              };
            }
            return ruler;
          });

          // Remove rulers with no colors
          return updatedRulers.filter((ruler) => ruler.colors.length > 0);
        });
      }
    }
  };

  // Update input values for the selected colors
  const handleInputChange = (fabricCode, colorCode, field, value) => {
    setAllSelectedRulers((prev) => {
      return prev.map((ruler) => {
        if (ruler.code === fabricCode) {
          return {
            ...ruler,
            colors: ruler.colors.map((color) => {
              if (color.colorCode === colorCode) {
                return {
                  ...color,
                  [field]: value || 0,
                };
              }
              return color;
            }),
          };
        }
        return ruler;
      });
    });
  };

  /* USEEFECTS */
  useEffect(() => {
    getAllRulers();
  }, [debouncedSearch, pagination.currentPage]);

  useEffect(() => {
    if (success) {
      handlePrint();
      setOrderData({ orderNumber: null, date: null });
      setAllSelectedRulers([]);
      setSelectedColors({});
      getAllRulers();
      setSearchQuery("");
    }
  }, [success]);

  // Sync selected rulers to display from our master list
  useEffect(() => {
    setSelectedRulers(allSelectedRulers);
  }, [allSelectedRulers]);

  return (
    <PageWrapper>
      {/* Back button */}
      <div>
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={
            userData?.userType === "user"
              ? "/invoiceManagement"
              : "/fabricstore-dashboard"
          }
        />
      </div>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500">
        <PageHeadline title="addOrder" classList="u-margin--top_700" />
        <div className="o-row o-row--gap-size_400 o-row--horizontal-align_space-between o-row--vertical-align_end">
          <div className="o-row o-row--gap-size_400">
            <CustomInput
              placeholder={t("search")}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              resetSearch={() => {
                setSearchQuery("");
              }}
              thick
            />
          </div>

          <div className="o-row o-row--gap-size_300">
            <CustomInput
              title={t("orderNumber") + ": "}
              value={orderData?.orderNumber}
              flex={false}
              readonly
              loading={loadingRulers}
            />
            <CustomInput
              title={t("Date") + ": "}
              value={orderData?.date}
              flex={false}
              readonly
              loading={loadingRulers}
            />
          </div>
        </div>

        <SectionDivider title={t("rulers")} />

        {loadingRulers && <Loader />}

        <OrderListWithInput
          data={fabricData}
          setSelectedRulers={setSelectedRulers}
          selectedRulers={selectedRulers}
          selectedColors={selectedColors}
          onColorSelect={handleColorSelection}
          measuringUnit={selectedMeasuringUnit}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          allSelectedRulers={allSelectedRulers}
        />

        {pagination && (
          <section className="pagination">
            <div className="pagination-buttons">
              <CustomButton
                variant="blank"
                title={t("previous")}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage - 1,
                  }))
                }
                disabled={pagination.currentPage === 1}
                icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                iconSize={15}
              />
              <span className="pagination-location">
                {t("page")}: {pagination.currentPage} - {pagination.totalPages}
              </span>
              <CustomButton
                title={t("next")}
                variant="blank"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage + 1,
                  }))
                }
                disabled={pagination.currentPage === pagination.totalPages}
                icon={currentDirection === "ltr" ? nextIcon : previousIcon}
                iconSize={15}
                reverseIcon
              />
            </div>
            <div className="pagination-buttons">
              <span className="pagination-location">
                {t("totalRecords")}: {pagination.totalRecords}
              </span>
            </div>
          </section>
        )}

        {allSelectedRulers.length > 0 && (
          <SectionDivider title={t("theAddedRulers")} />
        )}
        <FabricRulers
          data={allSelectedRulers}
          setSelectedRulers={setAllSelectedRulers}
          selectedColors={selectedColors}
          onColorSelect={handleColorSelection}
          measuringUnit={selectedMeasuringUnit}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
        />

        {confirmOrderLoading && <Loader />}
        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          {allSelectedRulers.length > 0 && (
            <>
              <CustomButton
                title={t("confirm")}
                onClick={handleConfirmOrder}
                icon={saveIcon}
                iconSize={16}
                disabled={confirmOrderLoading || isPrinting}
              />
            </>
          )}
        </div>
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setAreYouSure(false)}
          onConfirm={handlePrintAndSave}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm={true}
          confirmIconType="whiteCheck"
        />
      )}

      <PrintableOrderView
        ref={printRef}
        data={allSelectedRulers}
        currency={selectedCurrency}
        measuringUnit={selectedMeasuringUnit}
        orderDetails={orderDetails}
      />
    </PageWrapper>
  );
};

export default AddFabricOrderPage;
